import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable, of, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { InventoryItem } from "../../inventory/inventory";
import { EnvironmentConfig } from "../../core/core";

declare const environment: EnvironmentConfig;
export interface OpenAIConfig {
  apiKey?: string; // Made optional since we're using proxy
  model?: string;
  maxTokens?: number;
  temperature?: number;
  baseUrl?: string; // This will now point to our Admin API proxy
}

export interface OpenAIResponse {
  content: string;
  usage?: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
  model: string;
}

export interface OpenAIRequest {
  model: string;
  messages: Array<{
    role: "system" | "user" | "assistant";
    content: string;
  }>;
  max_tokens?: number;
  temperature?: number;
}

@Injectable({
  providedIn: "root",
})
export class OpenAIService {
  private config: OpenAIConfig = {
    // Remove API key since it's now handled by the backend
    model: "gpt-4.1",
    maxTokens: 1000,
    temperature: 0.7,
    // Update base URL to point to our Admin API proxy
    baseUrl: `${environment.adminApiDomain}/api/admin/v1/openai`,
  };

  constructor(private http: HttpClient) {}

  /**
   * Configure the OpenAI service with optional settings
   * Note: API key is no longer needed as it's handled by the backend
   */
  configure(config: Partial<OpenAIConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Send a prompt to OpenAI and get a response
   */
  sendPrompt(prompt: string, systemMessage?: string): Observable<OpenAIResponse> {
    const messages = [];

    if (systemMessage) {
      messages.push({
        role: "system" as const,
        content: systemMessage,
      });
    }

    messages.push({
      role: "user" as const,
      content: prompt,
    });

    const requestBody: OpenAIRequest = {
      model: this.config.model!,
      messages,
      max_tokens: this.config.maxTokens,
      temperature: this.config.temperature,
    };

    // No need for Authorization header since the backend handles it
    const headers = new HttpHeaders({
      "Content-Type": "application/json",
    });

    return this.http
      .post<any>(`${this.config.baseUrl}/chat/completions`, requestBody, { headers })
      .pipe(
        map(response => this.parseResponse(response)),
        catchError(error => {
          console.error("OpenAI API Error:", error);
          return throwError(() => new Error(`OpenAI API Error: ${error.error?.error?.message || error.message || "Unknown error"}`));
        }),
      );
  }

  /**
   * Send a simple prompt without system message
   */
  prompt(prompt: string): Observable<string> {
    return this.sendPrompt(prompt).pipe(map(response => response.content));
  }

  /**
   * Send a conversation with multiple messages
   */
  sendConversation(messages: Array<{ role: "user" | "assistant"; content: string }>, systemMessage?: string): Observable<OpenAIResponse> {
    const conversationMessages = [];

    if (systemMessage) {
      conversationMessages.push({
        role: "system" as const,
        content: systemMessage,
      });
    }

    conversationMessages.push(
      ...messages.map(msg => ({
        role: msg.role as "user" | "assistant",
        content: msg.content,
      })),
    );

    const requestBody: OpenAIRequest = {
      model: this.config.model!,
      messages: conversationMessages,
      max_tokens: this.config.maxTokens,
      temperature: this.config.temperature,
    };

    // No need for Authorization header since the backend handles it
    const headers = new HttpHeaders({
      "Content-Type": "application/json",
    });

    return this.http
      .post<any>(`${this.config.baseUrl}/chat/completions`, requestBody, { headers })
      .pipe(
        map(response => this.parseResponse(response)),
        catchError(error => {
          console.error("OpenAI API Error:", error);
          return throwError(() => new Error(`OpenAI API Error: ${error.error?.error?.message || error.message || "Unknown error"}`));
        }),
      );
  }

  /**
   * Parse the OpenAI API response
   */
  private parseResponse(response: any): OpenAIResponse {
    const choice = response.choices?.[0];
    if (!choice) {
      throw new Error("No response from OpenAI API");
    }

    return {
      content: choice.message?.content || "",
      usage: response.usage
        ? {
            promptTokens: response.usage.prompt_tokens,
            completionTokens: response.usage.completion_tokens,
            totalTokens: response.usage.total_tokens,
          }
        : undefined,
      model: response.model,
    };
  }

  /**
   * Get the current configuration
   */
  getConfig(): OpenAIConfig {
    return { ...this.config };
  }

  /**
   * Check if the service is properly configured
   * Note: API key check is no longer needed since backend handles authentication
   */
  isConfigured(): boolean {
    return true; // Always true since backend handles configuration
  }

  /**
   * Estimate transportation cost using OpenAI based on machine location and job zipcode
   */
  estimateTransportationCost(
    machine: InventoryItem,
    zipcode: string,
  ): Observable<{ min: number; max: number; average: number; distance: number; explanation: string } | null> {
    const machineLocation = machine.location;
    const machineAddress = machineLocation?.address;

    // Build location information for the prompt
    let machineLocationInfo = "";
    if (machineAddress) {
      machineLocationInfo = `${machineAddress.city}, ${machineAddress.stateProvince} ${machineAddress.postalCode}`;
    } else if (machineLocation?.name) {
      machineLocationInfo = machineLocation.name;
    } else {
      machineLocationInfo = "Unknown location";
    }

    const prompt = `Estimate the round-trip transportation cost for a construction equipment rental from ${machineLocationInfo} to zipcode ${zipcode}. 

Machine details:
- Type: ${machine.primaryType}
- Weight: ${machine.weight || "Unknown"} lbs

Please provide a cost range for the round-trip transportation cost in USD. Consider factors like:
- Distance between locations
- type of the machine
- weight of the machine (biggest factor)

Make sure the minimum and maximum cost range is not too wide.

Return the response in this exact format:
First line: four numbers separated by commas (minimum cost, maximum cost, average cost, estimated distance in miles)
Second line: 1-2 sentence explanation of how you calculated the estimate.  Dont include any city names or state names.

Example:
2000,3500,2750,150
Based on the 150-mile round trip and 25,000 lb excavator weight, estimated costs include fuel, labor, permits, and specialized transport equipment.`;

    return this.prompt(prompt).pipe(
      map(response => {
        // Split response into lines
        const lines = response.trim().split("\n");
        if (lines.length < 2) {
          return null;
        }

        // Parse first line for numbers
        const costMatches = lines[0].match(/\d+/g);
        if (costMatches && costMatches.length >= 4) {
          const min = parseInt(costMatches[0], 10);
          const max = parseInt(costMatches[1], 10);
          const average = parseInt(costMatches[2], 10);
          const distance = parseInt(costMatches[3], 10);
          const explanation = lines[1].trim();
          return { min, max, average, distance, explanation };
        }
        // Return null if parsing fails - no fallback
        return null;
      }),
      catchError(error => {
        console.error("Failed to estimate transportation cost:", error);
        // Return null on error - no fallback
        return of(null);
      }),
    );
  }
}
